import React from 'react';
import { motion } from 'framer-motion'; 
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './netflix-clone.css';
import netflixCloneImage from '../../../assets/images/netflixuiclone.png';

const NetflixUIClone = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Netflix <span>UI Clone</span>
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Last Updated On: 11th July 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={netflixCloneImage} alt="Netflix UI Clone" className="project-image" />
          <h2 className="project-subtitle">Netflix Clone</h2>
          <span className="project-category">Website</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="netflixClone" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="https://project-tap-assignment-10.netlify.app/" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/Netflix-Clone" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "netflixClone",
                title: "Netflix UI Clone",
                path: "/projects/projectdetail/netflix-clone"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
            Welcome to the <strong>Netflix UI Clone</strong>! Here's a detailed overview of the streaming platform interface and its features.
          </p>

          <h3 className="section-title">Overview 🌟</h3>
          <p className="overview-description">
            The <strong>Netflix UI Clone</strong> is a modern and responsive streaming platform inspired by Netflix. It recreates the cinematic browsing experience with featured banners, movie collections, TV show categories, search functionality, and a premium dark-themed interface while demonstrating modern front-end development practices.
          </p>

          <h3 className="section-title">Key Features 🛠️</h3>
          <ul className="feature-list">
            <li><strong>Hero Banner</strong> 🎬 - Featured movie section with an immersive cinematic background.</li>
            <li><strong>Movie Categories</strong> 🍿 - Browse Trending, Popular, Action, Comedy, Horror, and more.</li>
            <li><strong>Search Interface</strong> 🔍 - Discover movies and TV shows through a clean search experience.</li>
            <li><strong>Movie Cards</strong> 🎥 - Interactive cards with hover animations and movie previews.</li>
            <li><strong>Responsive Navigation</strong> 📱 - Optimized navigation for desktop, tablet, and mobile devices.</li>
            <li><strong>Dark Theme UI</strong> 🌙 - Premium Netflix-inspired interface with smooth animations.</li>
            <li><strong>Watchlist Interface</strong> ❤️ - Modern "My List" section for saved content.</li>
          </ul>

          <h3 className="section-title">How the Website Works 🔍</h3>
          <ul className="how-it-works-list">
            <li><strong>Browse Categories</strong> 🎞️ - Explore movies and TV shows by genre.</li>
            <li><strong>Search Content</strong> 🔍 - Quickly find your favorite entertainment.</li>
            <li><strong>View Details</strong> 🎬 - Open movie cards to explore information and previews.</li>
            <li><strong>Save Favorites</strong> ❤️ - Organize content using the watchlist interface.</li>
          </ul>

          <h3 className="section-title">Features & Benefits 🚀</h3>
          <ul className="benefits-list">
            <li><strong>Cinematic UI</strong> 🎨 - Modern streaming platform interface with premium visuals.</li>
            <li><strong>Responsive Experience</strong> 📱 - Seamless viewing experience across all devices.</li>
            <li><strong>Interactive Components</strong> 🎥 - Animated movie cards, banners, and navigation.</li>
            <li><strong>Modern User Experience</strong> ✨ - Smooth layouts with engaging browsing interactions.</li>
          </ul>

          <h3 className="section-title">Technical Details ⚙️</h3>
          <ul className="technical-details-list">
            <li><strong>Frontend</strong> 🖥️ - React.js, HTML5, CSS3, and JavaScript (ES6).</li>
            <li><strong>Animations</strong> ✨ - Framer Motion for smooth page transitions and hover effects.</li>
            <li><strong>Responsive Design</strong> 📱 - CSS Flexbox, Grid, and Media Queries.</li>
            <li><strong>Deployment</strong> ☁️ - Hosted on Netlify with GitHub for version control.</li>
          </ul>

          <h3 className="section-title">Real-Life Applications 🌍</h3>
          <ul className="real-life-applications-list">
            <li><strong>Streaming Platforms</strong> 🎬 - Online movie and TV streaming services.</li>
            <li><strong>Entertainment Websites</strong> 🍿 - Digital media and video content platforms.</li>
            <li><strong>Media Libraries</strong> 📺 - Organize and showcase multimedia collections.</li>
            <li><strong>Learning Modern UI Design</strong> 💻 - Practice responsive streaming platform development.</li>
          </ul>

          <h3 className="section-title">Future Features 🔮</h3>
          <ul className="future-features-list">
            <li><strong>User Authentication</strong> 🔐 - Secure login and personalized profiles.</li>
            <li><strong>Movie Database API</strong> 🎥 - Fetch real-time movies and TV shows dynamically.</li>
            <li><strong>Continue Watching</strong> ▶️ - Resume playback from the last watched position.</li>
            <li><strong>Personalized Recommendations</strong> 🤖 - AI-powered suggestions based on viewing history.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>
            The <strong>Netflix UI Clone</strong> recreates the immersive experience of a modern streaming platform with responsive layouts, cinematic hero banners, movie collections, search functionality, and interactive user interfaces. This project demonstrates advanced front-end development skills, responsive web design, and the implementation of a premium entertainment platform.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default NetflixUIClone;